import { Box, Typography, useTheme } from "@mui/material";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis, Label, LabelList } from "recharts";
import DownloadComponentButton from "../button/DownloadComponentButton";
import { formatTo2Decimals } from "../grid/columnDefinitions/columnDefinitions";
import { GRAPH_PALETTE } from "../../theme/style/graphChartPalette";

const BiaxialBarChart = ({
  data = [],
  name = "name",
  fields = [],
  colors = [GRAPH_PALETTE.lcaStages.A3, GRAPH_PALETTE.lcaStages.A4, GRAPH_PALETTE.lcaStages.D],
  title = "title",
}) => {
  const ref = useRef();
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const { t } = useTranslation();
  const [visibility, setVisibility] = useState(false);

  return (
    <Box onMouseEnter={() => setVisibility(true)} onMouseLeave={() => setVisibility(false)} sx={{ width: "100%", position: "relative" }}>
      <Box sx={{ position: "absolute", top: "5px", right: "5px", zIndex: 10 }}>
        <DownloadComponentButton ref={ref} filename={t(title) + ".png"} visibility={visibility} />
      </Box>
      <Box ref={ref} sx={{ p: 1, background: "transparent" }}>
        {title && (
          <Box sx={{ 
            display: "flex", 
            alignItems: "center", 
            px: 3, 
            py: 2, 
            borderBottom: 1, 
            borderColor: theme.palette.divider 
          }}>
            <Typography variant="Chart Title" sx={{ color: theme.palette.text.primary, fontWeight: 600 }}>
              {t(title)}
            </Typography>
          </Box>
        )}
        <ResponsiveContainer width="100%" height={400}>
          <BarChart data={data} margin={{ top: 30, right: 40, left: 40, bottom: 40 }}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} strokeOpacity={0.1} />
            <XAxis
              dataKey={name}
              tickFormatter={(v) => t(v)}
              axisLine={{ stroke: isDark ? GRAPH_PALETTE.axes.darkMode.line : GRAPH_PALETTE.axes.lightMode.line, strokeWidth: GRAPH_PALETTE.axes.strokeWidth }}
              tickLine={{ stroke: isDark ? GRAPH_PALETTE.axes.darkMode.tick : GRAPH_PALETTE.axes.lightMode.tick, strokeWidth: 1.5 }}
              tick={{ fontFamily: GRAPH_PALETTE.font.family, fontSize: parseInt(GRAPH_PALETTE.font.sizes.standard) || 12, fontWeight: GRAPH_PALETTE.font.weights.standard, fill: theme.palette.text.graphText || (isDark ? "#ffffff" : "#333333") }}
            >
              <Label value={t("Target Entity")} offset={-15} position="insideBottom" style={{ fill: theme.palette.text.graphText || (isDark ? "#ffffff" : "#333333"), fontSize: parseInt(GRAPH_PALETTE.font.sizes.axisLabel) || 12, fontWeight: GRAPH_PALETTE.font.weights.axisLabel, fontFamily: GRAPH_PALETTE.font.family }} />
            </XAxis>
            <YAxis
              yAxisId="left"
              orientation="left"
              stroke={colors[0]}
              axisLine={{ stroke: colors[0], strokeWidth: GRAPH_PALETTE.axes.strokeWidth }}
              tickLine={{ stroke: colors[0], strokeWidth: 1.5 }}
              tick={{ fontFamily: GRAPH_PALETTE.font.family, fontSize: parseInt(GRAPH_PALETTE.font.sizes.standard) || 12, fontWeight: GRAPH_PALETTE.font.weights.standard, fill: colors[0] }}
              tickFormatter={(v) => formatTo2Decimals(v)}
            >
              <Label value={t(fields[0]?.label || "")} angle={-90} position="insideLeft" offset={-10} style={{ fill: colors[0], fontSize: parseInt(GRAPH_PALETTE.font.sizes.axisLabel) || 12, fontWeight: GRAPH_PALETTE.font.weights.axisLabel, fontFamily: GRAPH_PALETTE.font.family }} />
            </YAxis>
            <YAxis
              yAxisId="right"
              orientation="right"
              stroke={colors[1] || colors[0]}
              axisLine={{ stroke: colors[1] || colors[0], strokeWidth: GRAPH_PALETTE.axes.strokeWidth }}
              tickLine={{ stroke: colors[1] || colors[0], strokeWidth: 1.5 }}
              tick={{ fontFamily: GRAPH_PALETTE.font.family, fontSize: parseInt(GRAPH_PALETTE.font.sizes.standard) || 12, fontWeight: GRAPH_PALETTE.font.weights.standard, fill: colors[1] || colors[0] }}
              tickFormatter={(v) => formatTo2Decimals(v)}
            >
              <Label value={t(fields[1]?.label || "")} angle={90} position="insideRight" offset={-10} style={{ fill: colors[1] || colors[0], fontSize: parseInt(GRAPH_PALETTE.font.sizes.axisLabel) || 12, fontWeight: GRAPH_PALETTE.font.weights.axisLabel, fontFamily: GRAPH_PALETTE.font.family }} />
            </YAxis>
            <Tooltip
              formatter={(value, name) => [formatTo2Decimals(value), t(name)]}
              contentStyle={{
                fontFamily: GRAPH_PALETTE.font.family,
                fontSize: parseInt(GRAPH_PALETTE.font.sizes.standard) || 14,
                color: "#333",
                borderRadius: "8px",
              }}
            />
            <Legend
              formatter={(value) => t(value)}
              layout="horizontal"
              verticalAlign="bottom"
              align="center"
              iconType="circle"
              iconSize={18}
              wrapperStyle={{
                fontFamily: GRAPH_PALETTE.font.family,
                fontSize: parseInt(GRAPH_PALETTE.font.sizes.standard) || 14,
                bottom: 0,
              }}
            />
            {fields.map((f, i) => (
              <Bar key={i} yAxisId={f.axis} dataKey={f.name} fill={colors[i % colors.length]} name={t(f.label)} radius={[6, 6, 0, 0]} barSize={40} />
            ))}
          </BarChart>
        </ResponsiveContainer>
      </Box>
    </Box>
  );
};

export default BiaxialBarChart;
